(() => {
  'use strict';
  const storageKey = 'dac.warranty.receipt.v1';
  const maxProducts = 20;
  const receiptAge = 24 * 60 * 60 * 1000;
  const validReceipt = receipt => {
    if (!receipt || typeof receipt.reference !== 'string' || !/^[a-zA-Z0-9_-]{8,128}$/.test(receipt.reference)) return false;
    if (typeof receipt.registeredAt !== 'string' || !Number.isFinite(Date.parse(receipt.registeredAt))) return false;
    if (!Array.isArray(receipt.products) || !receipt.products.length || receipt.products.length > maxProducts) return false;
    return receipt.products.every(product => product && ['name', 'retailer', 'purchasedOn', 'orderReference'].every(key => typeof product[key] === 'string' && product[key].trim().length > 0 && product[key].length <= 1000) && /^\d{4}-\d{2}-\d{2}$/.test(product.purchasedOn));
  };
  const displayDate = (value, dateOnly = false) => {
    const date = new Date(dateOnly ? `${value}T12:00:00` : value);
    return Number.isFinite(date.getTime()) ? date.toLocaleDateString(document.documentElement.dataset.dateLocale || document.documentElement.lang || undefined, {year:'numeric', month:'long', day:'numeric'}) : value;
  };
  function renderReceipt(root, receipt) {
    if (!root || !validReceipt(receipt)) return false;
    root.querySelector('[data-receipt-reference]').textContent = receipt.reference;
    root.querySelector('[data-receipt-date]').textContent = displayDate(receipt.registeredAt);
    const products = root.querySelector('[data-receipt-products]');
    products.replaceChildren();
    receipt.products.forEach(product => {
      const article = document.createElement('article');
      article.className = 'dac-wr-receipt__product';
      const title = document.createElement('h4');
      title.textContent = product.name;
      article.append(title);
      const list = document.createElement('dl');
      ['retailer','purchasedOn','orderReference'].forEach(key => {
        const group = document.createElement('div');
        const term = document.createElement('dt');
        const definition = document.createElement('dd');
        term.textContent = root.querySelector(`[data-label="${key}"]`).textContent;
        definition.textContent = key === 'purchasedOn' ? displayDate(product[key], true) : product[key];
        group.append(term, definition);
        list.append(group);
      });
      article.append(list);
      products.append(article);
    });
    root.hidden = false;
    const printButton = root.querySelector('[data-receipt-print]');
    if (!printButton.dataset.ready) {
      printButton.dataset.ready = 'true';
      printButton.addEventListener('click', () => window.print());
    }
    return true;
  }
  function initializeReceipt(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    try {
      const requested = new URL(window.location.href).searchParams.get('registration');
      const stored = JSON.parse(sessionStorage.getItem(storageKey));
      if (!stored || !Number.isFinite(stored.savedAt) || Date.now() - stored.savedAt > receiptAge || stored.savedAt > Date.now() || !validReceipt(stored.receipt) || stored.receipt.reference !== requested) return;
      if (!renderReceipt(root.querySelector('[data-warranty-receipt]'), stored.receipt)) return;
      root.querySelector('[data-thanks-title]').textContent = root.querySelector('[data-confirmed-title]').textContent;
      root.querySelector('[data-thanks-lead]').textContent = root.querySelector('[data-confirmed-lead]').textContent;
      root.querySelector('[data-unconfirmed-action]').hidden = true;
    } catch (_) {}
  }
  function initializeForm(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const form = root.querySelector('[data-warranty-form]');
    if (!form || !window.fetch || !window.FormData || !window.URLSearchParams || !window.AbortController || !window.crypto?.getRandomValues) return;
    const message = key => root.querySelector(`[data-message="${key}"]`)?.textContent || '';
    const keyField = form.elements.namedItem('idempotencyKey');
    if (!keyField.value) {
      if (window.crypto.randomUUID) keyField.value = window.crypto.randomUUID();
      else {
        const bytes = window.crypto.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 15) | 64;
        bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, item => item.toString(16).padStart(2,'0')).join('');
        keyField.value = `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
      }
    }
    const button = form.querySelector('[type="submit"]');
    const idleLabel = button.textContent;
    const errorPanel = form.querySelector('[data-warranty-errors]');
    const errorText = form.querySelector('[data-warranty-error-message]');
    const errorFields = form.querySelector('[data-warranty-error-fields]');
    const status = form.querySelector('[data-warranty-status]');
    const products = form.querySelector('.products-container');
    const addButton = form.querySelector('.js-add-product');
    const productTemplate = products.firstElementChild.cloneNode(true);
    let productIndex = 1;
    let pending = false;
    let completed = false;
    const clearErrors = () => {
      errorPanel.hidden = true;
      errorText.textContent = '';
      errorFields.replaceChildren();
      form.querySelectorAll('[aria-invalid="true"]').forEach(input => input.removeAttribute('aria-invalid'));
    };
    const resolveField = key => {
      const match = /^products\.(\d+)\.(productName|purchaseLocation|purchaseDate|orderReference|name|retailer|purchasedOn)$/.exec(key);
      if (match) {
        const names = {name:'productName',retailer:'purchaseLocation',purchasedOn:'purchaseDate'};
        return products.children[Number(match[1])]?.querySelector(`[name="${names[match[2]] || match[2]}"]`);
      }
      return Array.from(form.elements).find(field => field.name === key && !field.hidden && field.type !== 'hidden');
    };
    const showError = (text, fields) => {
      errorText.textContent = text;
      if (fields && typeof fields === 'object' && !Array.isArray(fields)) Object.entries(fields).slice(0,100).forEach(([key, value]) => {
        const input = resolveField(key);
        if (!input || typeof value !== 'string') return;
        input.setAttribute('aria-invalid','true');
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${input.id}`;
        link.textContent = value.slice(0,500);
        link.addEventListener('click', event => {
          event.preventDefault();
          const disclosure = input.closest('details');
          if (disclosure) disclosure.open = true;
          input.focus();
        });
        item.append(link);
        errorFields.append(item);
      });
      errorPanel.hidden = false;
      errorPanel.focus();
    };
    const updateProductLimit = () => {
      const atLimit = products.children.length >= maxProducts;
      addButton.disabled = pending || atLimit;
      form.querySelector('[data-product-limit]').hidden = !atLimit;
    };
    addButton.hidden = false;
    addButton.addEventListener('click', () => {
      if (pending || completed || products.children.length >= maxProducts) return;
      productIndex += 1;
      const product = productTemplate.cloneNode(true);
      product.querySelectorAll('input').forEach(input => {
        const originalId = input.id;
        input.id = `${originalId}-${productIndex}`;
        input.value = '';
        input.removeAttribute('aria-invalid');
        product.querySelector(`label[for="${originalId}"]`).htmlFor = input.id;
      });
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'dac-wr-remove';
      remove.textContent = message('remove_product');
      remove.addEventListener('click', () => {
        if (pending) return;
        product.remove();
        updateProductLimit();
        addButton.focus();
      });
      product.append(remove);
      products.append(product);
      updateProductLimit();
      product.querySelector('input').focus();
    });
    const country = form.elements.namedItem('country');
    const provinceText = form.querySelector('#wr-province');
    const provinceSelect = form.querySelector('#wr-province-select');
    const provinceLabel = form.querySelector('label[for="wr-province"]');
    country.addEventListener('change', () => {
      let zones = [];
      try { zones = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) {}
      if (!Array.isArray(zones)) zones = [];
      provinceSelect.replaceChildren();
      provinceSelect.append(new Option(message('select_province'),''));
      zones.forEach(zone => { if (Array.isArray(zone) && typeof zone[0] === 'string' && typeof zone[1] === 'string') provinceSelect.append(new Option(zone[1],zone[0])); });
      const hasZones = provinceSelect.options.length > 1;
      provinceText.hidden = hasZones;
      provinceText.disabled = hasZones;
      provinceSelect.hidden = !hasZones;
      provinceSelect.disabled = !hasZones;
      provinceLabel.htmlFor = hasZones ? provinceSelect.id : provinceText.id;
    });
    const phone = form.elements.namedItem('phone');
    if (window.intlTelInput) window.intlTelInput(phone, {initialCountry:'us',utilsScript:form.dataset.phoneUtils});
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (pending || completed) return;
      clearErrors();
      if (!form.reportValidity()) return;
      const payload = new URLSearchParams(new FormData(form));
      pending = true;
      const disabledStates = Array.from(form.elements).map(field => [field, field.disabled]);
      disabledStates.forEach(([field]) => { field.disabled = true; });
      button.textContent = message('submitting');
      form.setAttribute('aria-busy','true');
      status.textContent = message('submitting');
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(),30000);
      try {
        const response = await fetch(form.action, {method:'POST',headers:{Accept:'application/json','Content-Type':'application/x-www-form-urlencoded;charset=UTF-8'},body:payload.toString(),credentials:'same-origin',signal:controller.signal});
        let body;
        try { body = await response.json(); } catch (_) { throw new Error('invalid_response'); }
        if (!response.ok || body?.ok !== true) {
          const code = body?.error?.code;
          const known = {VALIDATION_ERROR:'validation_error',RATE_LIMITED:'rate_limit',IDEMPOTENCY_CONFLICT:'idempotency_conflict'};
          const copyKey = known[code] || (response.status === 429 ? 'rate_limit' : response.status === 422 ? 'validation_error' : 'service_error');
          const error = new Error(copyKey);
          error.fields = body?.error?.fields;
          throw error;
        }
        if (!validReceipt(body.receipt)) throw new Error('invalid_response');
        completed = true;
        status.textContent = message('success');
        let destination;
        try {
          if (form.dataset.thanksUrl) {
            destination = new URL(form.dataset.thanksUrl,window.location.origin);
            if (destination.origin !== window.location.origin) destination = null;
          }
          sessionStorage.setItem(storageKey,JSON.stringify({savedAt:Date.now(),receipt:body.receipt}));
        } catch (_) { destination = null; }
        if (destination) {
          destination.searchParams.set('registration',body.receipt.reference);
          window.location.assign(destination.href);
        } else {
          const inline = root.querySelector('[data-warranty-inline-receipt]');
          renderReceipt(inline.querySelector('[data-warranty-receipt]'),body.receipt);
          inline.hidden = false;
          form.hidden = true;
          inline.querySelector('[data-receipt-heading]').focus();
        }
      } catch (error) {
        showError(message(error.message) || message('network_error'),error.fields);
      } finally {
        window.clearTimeout(timeout);
        pending = false;
        form.removeAttribute('aria-busy');
        if (!completed) {
          disabledStates.forEach(([field, disabled]) => { field.disabled = disabled; });
          button.textContent = idleLabel;
          status.textContent = '';
          updateProductLimit();
        }
      }
    });
  }
  const initialize = () => {
    document.querySelectorAll('[data-warranty-registration]').forEach(initializeForm);
    document.querySelectorAll('[data-warranty-thanks]').forEach(initializeReceipt);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',initialize,{once:true});
  else initialize();
  document.addEventListener('shopify:section:load',initialize);
})();
